import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  acquireToken,
  getActiveAccount,
  initMsal,
  login as msalLogin,
  logout as msalLogout,
  type MsalRuntimeConfig,
} from '@/lib/msal';

export type AppRole =
  | 'Admin'
  | 'ProfileManager'
  | 'ProfileAdmin'
  | 'Auditor'
  | 'HelpDesk';

interface RuntimeBootstrap extends MsalRuntimeConfig {
  adEnabled: boolean;
  qrEmailEnabled?: boolean;
  // The org's licence-driving extension attribute slot (mapped in Settings), or
  // null when not mapped. Lets the user page label and show the licence value.
  licensingExtensionAttribute?: string | null;
  // The extensionAttribute slot that carries the managedBy marker (e.g.
  // "extensionAttribute1"). UserDetail checks this exact slot rather than
  // scanning every slot for the marker value. Optional so an older/absent
  // backend degrades gracefully.
  managedByExtensionSlot?: string | null;
  // Joiner UPN naming template (tokens {preferredName}/{surname}/{givenName}/
  // {initial}/{givenInitial}). Optional: absent on deployments whose backend
  // predates the surfaced pattern, in which case the UPN preview falls back to
  // the built-in {preferredName}.{surname} shape.
  naming?: { pattern?: string | null } | null;
  // How every date on every screen is written (spec §10). Absent on a backend
  // that predates the setting, in which case the formatter falls back to the
  // same default the server would have sent.
  display?: { dateFormat?: string | null } | null;
}

interface MePayload {
  oid: string;
  upn: string;
  displayName: string | null;
  roles: AppRole[];
  // False when the role was granted per-profile (scoped) rather than via the
  // global role-group mapping. Drives cross-profile affordances only - the
  // server enforces regardless.
  globalProfileAdmin?: boolean;
  globalProfileManager?: boolean;
  hasOverage: boolean;
}

export const useAuthStore = defineStore('auth', () => {
  const ready = ref(false);
  const initError = ref<string | null>(null);
  const runtime = ref<RuntimeBootstrap | null>(null);

  const oid = ref<string | null>(null);
  const upn = ref<string | null>(null);
  const displayName = ref<string | null>(null);
  const roles = ref<AppRole[]>([]);
  const globalProfileAdmin = ref(false);
  const globalProfileManager = ref(false);
  const hasOverage = ref(false);

  const signedIn = computed(() => oid.value !== null);

  async function bootstrap(): Promise<void> {
    if (ready.value) return;
    try {
      const cfgRes = await fetch('/api/config');
      if (!cfgRes.ok) throw new Error(`config fetch failed (${cfgRes.status})`);
      const cfg = (await cfgRes.json()) as RuntimeBootstrap;
      runtime.value = cfg;

      await initMsal({ tenantId: cfg.tenantId, clientId: cfg.clientId, apiScope: cfg.apiScope });

      if (getActiveAccount()) {
        await refreshIdentity();
      }
      ready.value = true;
    } catch (err) {
      initError.value = err instanceof Error ? err.message : 'bootstrap failed';
      ready.value = true;
    }
  }

  async function refreshIdentity(): Promise<void> {
    const token = await acquireToken();
    const res = await fetch('/api/me', {
      headers: { authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`/api/me failed (${res.status})`);
    const me = (await res.json()) as MePayload;
    oid.value = me.oid;
    upn.value = me.upn;
    displayName.value = me.displayName;
    roles.value = me.roles;
    // Fall back to role presence for an API that predates the flags.
    globalProfileAdmin.value = me.globalProfileAdmin ?? me.roles.includes('ProfileAdmin');
    globalProfileManager.value = me.globalProfileManager ?? me.roles.includes('ProfileManager');
    hasOverage.value = me.hasOverage;
  }

  async function signIn(): Promise<void> {
    await msalLogin();
  }

  async function signOut(): Promise<void> {
    oid.value = null;
    upn.value = null;
    displayName.value = null;
    roles.value = [];
    globalProfileAdmin.value = false;
    globalProfileManager.value = false;
    await msalLogout();
  }

  function hasRole(...allowed: AppRole[]): boolean {
    return roles.value.some((r) => allowed.includes(r));
  }

  return {
    ready,
    initError,
    runtime,
    oid,
    upn,
    displayName,
    roles,
    globalProfileAdmin,
    globalProfileManager,
    hasOverage,
    signedIn,
    bootstrap,
    refreshIdentity,
    signIn,
    signOut,
    hasRole,
  };
});
